/**
 * Web/electron half of the uplifted `data/constants` slice of surface.
 *
 * `shared.ts` owns what the two apps agree on; this file owns web's genuinely divergent
 * definitions. Add the next required symbol to whichever file is correct for it, rather than
 * creating a per-symbol module (CLAUDE.md → "Uplifting a module's dependencies").
 */
export * from "./shared";
/**
 * ⚠️ DIVERGENT — web's `NOTIFICATION_VARIABLES` has ELEVEN keys, mobile's has five.
 * Mobile's is a strict subset (same five keys, same five values); the six web-only keys are
 * `unRead`, `rejected`, `approved`, `apply`, `reject` and `reset`. Both definitions were
 * uplifted so the symbol resolves on both platforms, but they are NOT merged — mobile has no
 * `rejected`/`approved`, and web's `NotificationsSlice` reducers read exactly those two.
 * See index.native.ts for mobile's.
 *
 * The `: any` annotation is preserved verbatim from apps/web — consumers index it with keys
 * this object does not always carry, so narrowing it here would break them.
 */
export const NOTIFICATION_VARIABLES: any = {
  itemStatus: "item-status",
  selectAll: "selectAll",
  offline: "offline",
  instore: "instore",
  read: "Read",
  unRead: "Unread",
  rejected: "Rejected",
  approved: "Approved",
  apply: "apply",
  reject: "reject",
  reset: "reset"
};

/** Web-only — the localStorage key apiService throttles ERR_NETWORK toasts with. */
export const LAST_NETWORK_ERROR = 'lastNetworkError';

/**
 * Web-only — NW-app (the legacy Android WebView shell) version gates. Mobile has no such
 * shell and defines neither constant; verified by grep across `apps/mobile/src`.
 *
 * Only `LS_BRIDGE` is read by anything in core so far (`helpers/localServer`'s
 * `getLSRequestPayloadData`), but the whole object is uplifted verbatim rather than the one
 * key: it is a single named constant in the app, and splitting it per-key would fork the
 * definition. Comments preserved from apps/web.
 */
export const MIN_NW_APP_VERSION_SUPPORTED = {
  /**
   * Minimum version of the NW app to support auto update feature
   */
  AUTO_UPDATE: 12, //Previously it was 7, had to update it because auto update started breaking for version 1.0.0.12.

  /**
   * Minimum version of the NW app to support local server bridge
   */
  LS_BRIDGE: 6,

  /**
   * WebSocket implementation version.
   */
  NW_WEBSOCKET_IMPLEMENTATION_VERSION: 14,

  /**
   * Full installer update implementation version.
   */
  NW_FULL_INSTALLER_UPDATE_IMPLEMENTATION_VERSION: 16,

};

/**
 * Latest stable version of the NW app
 */
export const NW_APP_LATEST_STABLE_VERSION = 19;

/**
 * Web-only — the event keys/actions of the legacy NW-app (Android WebView shell) bridge.
 * `nativeHooks.exec(key, action, …)` takes one of each. Mobile defines neither: it reaches the
 * native layer through `execMobileServer`/`ristadb`, not through named chrome.* events.
 * Verified by grep across `apps/mobile/src`.
 *
 * `as const` preserved verbatim — `utils/OfflineUtil` indexes `NW_APP_EVENT_ACTIONS.ristadb.put`
 * and friends, and widening these to `string` would lose that checking.
 */
export const NW_APP_EVENT_KEYS = {
  printer: "chrome.printer",
  ristadb: "chrome.ristadb",
  networkinfo: "chrome.networkinfo",
  server: "chrome.server",
  push: "chrome.push",
  dualscreen: "chrome.dualscreen",
  weighingScale: "chrome.weighing_scale",
  appversion: "app.version",
  appversionnew: "app.version.new",
  appUpdate: "appUpdate",
  restartApp: "restartApp",
  appInstallerUpdate: "appInstallerUpdate",
  appUpdateCodeAvailable: "appUpdateCodeAvailable",
  windowClose: "window.close",
} as const;

export const NW_APP_EVENT_ACTIONS = {
  printer: {
    discoverBluetoothDevices: "discoverBluetoothDevices",
    discoverUSBDevices: "discoverUSBDevices",
    requestUSBPermission: "requestUSBPermission",
    discoverLocalDevices: "discoverLocalDevices",
    print: "print",
  },
  server: {
    error: "error",
    start: "start",
    stop: "stop",
    getIpV4: "getIpV4",
    serverStatus: "serverStatus",
    post: "post",
  },
  push: {
    register: "register",
    stop: "stop",
    registration: "registration",
    notification: "notification",
  },
  ristadb: {
    delete: "delete",
    get: "get",
    replace: "replace",
    put: "put",
  },
  // NW.js only — Electron uses the typed electronAPI.customerDisplay methods.
  // Window lifecycle only: cart data goes renderer-to-renderer over a
  // BroadcastChannel and never touches either shell.
  dualscreen: {
    open: "open",
    close: "close",
  },
  // USB weighing scale. These names are NOT ours to choose — the NW.js shell already
  // implements them (`build_nw_new/src/js/chrome/weighing_scale.js`, routed at
  // `js/chrome/index.js:1321`), so the Sencha wire contract is the naming authority and
  // that backend is reused unchanged. Electron deliberately does NOT go through this
  // key/action pair: it exposes typed `electronAPI.weighingScale.*` methods, the same way
  // `printer` and `server` do. Args are `[{ portPath }]`; the reply is
  // `{ id, success, weight, error? }`.
  weighingScale: {
    discoverSerialPorts: "discoverSerialPorts",
    readSerialPort: "readSerialPort",
    stopSerialPort: "stopSerialPort",
  },
} as const;

/**
 * Event names the web app posts to a hosting React Native shell.
 *
 * **WEB-ONLY: `apps/mobile/src/data/constants.ts` defines no such constant** (verified by grep,
 * not assumed — see hazard 32). Same situation as `NW_APP_EVENT_KEYS`/`NW_APP_EVENT_ACTIONS`
 * above, and it lives here for the same reason.
 *
 * Moved WHOLE rather than seeded with the one key `saga/itemStatus` needs
 * (`soldOutItemDetail`): it is a single symbol with ~20 app call sites, so splitting it would
 * create exactly the two-definitions-drifting-apart problem the ledger's "one definition,
 * always" rule exists to prevent. `as const` is preserved — call sites index it by literal key.
 */
export const REACT_NATIVE_APP_EVENTS = {
  setPrinter: "setPrinter",
  printTestBill: "printTestBill",
  printBill: "printBill",
  setDefaultPrinter: "setDefaultPrinter",
  setLocalstorage: "setLocalstorage",
  deleteBlePrinter: "deleteBlePrinter",
  // ⚠️ ADDED 2026-09-15, and it is the counterpart `deleteBlePrinter` always lacked. The single
  // USB printer lives in a device-local `usbPrinter` key on BOTH sides of the webview bridge;
  // deleting it here only cleared this side's copy, so the react-native host kept offering a
  // printer the merchant had removed. See `apps/mobile/src/hooks/usePrinter/utils.js`.
  deleteUsbPrinter: "deleteUsbPrinter",
  getPreviousConnectionDetails: "getPreviousConnectionDetails",
  saveDefaultModuleSettings: "reactNative.saveDefaultModuleSettings",
  reducerUpdate: "reactNative.reducerUpdate",
  getReducerState: "reactNative.getReducerState",
  pinelabsPayments: "pinelabsPayments",
  scanBluetoothDevices: "scanBluetoothDevices",
  scanUsbDevices: "scanUsbDevices",
  soldOutItemDetail: "soldOutItemDetail",
  pinelabsPrintBill: "pinelabsPrintBill",
  webviewAction: "reactNative.webviewAction",

  MERCHANT_LOGIN_ACTION: 'MERCHANT_LOGIN_ACTION',
  SUPPORT_LOGIN_SUCCESS_ACTION: 'SUPPORT_LOGIN_SUCCESS_ACTION',
  CHANGE_BUSINESS_BACK: 'CHANGE_BUSINESS_BACK',
  LOGOUT : 'logout',
} as const;

// `SALE_SOURCE_TYPES` moved to `./shared.ts` on 2026-09-07 — `loyaltyRefundEffect` now runs on
// both platforms and reads it, and a constant absent from one variant is a silent runtime
// `undefined` there. Still exported from here by the `export * from "./shared"` above.

// `ITEM_SOLD_OUT` moved to `./shared.ts` on 2026-09-29 — My Orders v2's `holdOrder` reads it on
// both platforms. Still exported from here by the `export * from "./shared"` above.

// `EXTERNAL_LOYALTY_PARTNERS` and its `_C1` / `_C2` / `_C3` class arrays,
// `EXTERNAL_LOYALTY_PROVIDER_METAIDS`, `LOYALTY_PROGRAM_TYPE` and `IS_SUGGESTIVE_SELLING_V2_ENABLED`
// all moved to `./shared.ts` — the loyalty flow and the Suggestive Selling flow are both shared by
// the two platforms now, so the data they classify on and the switch that picks the engine version
// have to be too. Every one is re-exported by the `export * from "./shared"` above; importers here
// are unchanged.

/** Picklist identifiers. **WEB-ONLY.** */
export const LIST_TYPES = {
  PHONE_COUNTRIES: 'phone-countries',
} as const
